import { Badge, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faPlusCircle, faTrash, faWrench } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { WorklistNode } from '../../reducers/worklistReducer';
import * as worklistActions from '../../actions/worklistActions';
import WorklistNodeForm from './WorklistNodeForm';
import WorklistTestForm from './WorklistTestForm';
import useToaster from '../../actions/useToaster';
import { useAppDispatch } from '../../hooks';
import { useSelector } from 'react-redux';
import { getWorklistNodes } from '../../reducers/selectors';
import { canPerformAction } from '../../actions/userActions';
import { availableOperations, User } from '../../reducers/userReducer';

interface IWorklistNodes {
  user: User;
}

export default function WorklistNodesTable({ user }: IWorklistNodes) {
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const worklistNodes = useSelector(getWorklistNodes);
  const authorized = canPerformAction(user, availableOperations.CAN_MANAGE_WORKLIST_NODES);

  const [editingNode, setEditingNode] = useState<WorklistNode | null>(null);
  const [creatingNode, setCreatingNode] = useState<boolean>(false);
  const [deletingNode, setDeletingNode] = useState<WorklistNode | null>(null);
  const [testWorkList, setTestWorkList] = useState<boolean>(false);
  const [anyNode, setAnyNode] = useState(false);

  useEffect(() => {
    dispatch(worklistActions.getWorklistNodes());
  }, []);

  useEffect(() => {
    setAnyNode(worklistNodes.some(node => node.enabled));
  }, [worklistNodes]);

  return (
    <div id="worklist-nodes-table">
      <h5>Nodes</h5>
      <div className="worklist-table-header">
        <span>Worklist queries will be forwarded to the following nodes:</span>
        <div id="worklist-nodes-actions">
          <Button
            disabled={!authorized}
            size="sm"
            style={{ marginLeft: '2em' }}
            variant="primary"
            onClick={() => setCreatingNode(true)}
          >
            Add new node <FontAwesomeIcon icon={faPlusCircle} style={{ marginLeft: '5px' }} />
          </Button>

          <Button
            disabled={!authorized || !anyNode}
            size="sm"
            style={{ marginLeft: '2em' }}
            variant="primary"
            onClick={() => setTestWorkList(true)}
          >
            Test worklist <FontAwesomeIcon icon={faBolt} style={{ marginLeft: '5px' }} />
          </Button>
        </div>
      </div>

      <table className="queue-table">
        <thead>
          <tr>
            <th style={{ width: '13%' }} />
            <th style={{ width: '13%' }} />
            <th />
            <th style={{ width: '13%' }} />
            <th style={{ width: '13%' }} />
          </tr>
        </thead>
        <tbody>
          {worklistNodes.map((node, index) => (
            <tr key={index} className="node-row">
              <td>
                <b>{node.displayName}</b>
              </td>
              <td>{node.forwardMethod}</td>
              {'DICOM' === node.forwardMethod && <td title={node.dicomUrl}>{node.dicomUrl}</td>}
              {'UPS_RS' === node.forwardMethod && <td title={node.upsRsEndpoint}>{node.upsRsEndpoint}</td>}
              <td>{node.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="danger">Disabled</Badge>}</td>
              <td>
                <div style={{ width: '100%', textAlign: 'right', overflow: 'visible' }}>
                  {authorized && (
                    <FontAwesomeIcon icon={faWrench} className="clickable-icon" onClick={e => setEditingNode(node)} />
                  )}
                  {authorized && (
                    <FontAwesomeIcon icon={faTrash} className="clickable-icon" onClick={e => setDeletingNode(node)} />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editingNode && (
        <EditWorklistNodePopup
          node={editingNode}
          onClose={() => setEditingNode(null)}
          onSave={(node: WorklistNode) => {
            console.log('updating worklist node', node);
            worklistActions
              .updateWorklistNode(node)
              .then(() => {
                dispatch(worklistActions.forceGetWorklistNodes());
                setEditingNode(null);
                toaster.success('Worklist node updated');
              })
              .catch(error => {
                console.log('error updating worklist node', error);
                toaster.error('Error updating worklist node');
              });
          }}
        />
      )}

      {creatingNode && (
        <InsertWorklistNodePopup
          onClose={() => setCreatingNode(false)}
          onSave={(node: WorklistNode) => {
            console.log('creating worklist node', node);
            worklistActions
              .insertWorklistNode(node)
              .then(() => {
                dispatch(worklistActions.forceGetWorklistNodes());
                setCreatingNode(false);
                toaster.success('Worklist node created');
              })
              .catch(error => {
                console.log('error adding worklist node', error);
                toaster.error('Error adding worklist node');
              });
          }}
        />
      )}

      {deletingNode && (
        <DeleteWorklistNodePopup
          node={deletingNode}
          onClose={() => setDeletingNode(null)}
          onConfirm={() => {
            if (deletingNode) {
              worklistActions
                .deleteWorklistNode(deletingNode)
                .then(() => {
                  dispatch(worklistActions.forceGetWorklistNodes());
                  setDeletingNode(null);
                  toaster.success('Worklist node deleted');
                })
                .catch(error => {
                  console.log('error deleting worklist node', error);
                  toaster.error('Error deleting worklist node');
                });
            }
          }}
        />
      )}

      {testWorkList && <TestWorkListPopup onClose={() => setTestWorkList(false)} />}
    </div>
  );
}

const InsertWorklistNodePopup = ({ onClose, onSave }) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>New Worklist node</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <WorklistNodeForm
        node={{ forwardMethod: 'UPS_RS', enabled: true }}
        onClose={onClose}
        onSave={onSave}
        saveButtonLabel="Add"
      />
    </Modal.Body>
  </Modal>
);

const EditWorklistNodePopup = ({ node, onClose, onSave }) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>Editing Worklist node {node.displayName}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <WorklistNodeForm node={node} onClose={onClose} onSave={onSave} saveButtonLabel="Save changes" />
    </Modal.Body>
  </Modal>
);

const DeleteWorklistNodePopup = ({ node, onClose, onConfirm }) => (
  <Modal show>
    <Modal.Header>
      <Modal.Title>Deleting Worklist node {node.displayName}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        You are going to delete the Worklist node <strong>{node.displayName}</strong>
      </p>
      <p>Are you sure?</p>
      <div style={{ textAlign: 'right', marginTop: '1em' }}>
        <br />
        <Button variant="outline-secondary" onClick={onClose} style={{ marginRight: '5px' }}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          Confirm delete
        </Button>
      </div>
    </Modal.Body>
  </Modal>
);

const TestWorkListPopup = ({ onClose }) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>Test worklist</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <WorklistTestForm onClose={onClose} />
    </Modal.Body>
  </Modal>
);
